import React from 'react';
import { Flex, Card, CardContent, Heading, Text, Button, ThemeProvider, Divider, TextField, PasswordField, Spinner, PasswordStrengthIndicator, usePasswordStrength } from '@chatsdet/ui';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useEmailSignup, authErrorMessage, isVerificationEmailSendFailure } from '../../hooks/useEmailAuth';
import type { SignUpSearchParams } from '../../features/auth/routes';
import { useInviteToken } from '../../hooks/useInviteToken';
import { BRAND } from '../../brand';
import { CofactorMark } from '../components/CofactorMark';

export function SignUpPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as SignUpSearchParams;
  const inviteToken = useInviteToken(search.invite);
  const { name: brandName, termsUrl, privacyUrl } = BRAND;

  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  const signupMutation = useEmailSignup();
  const { valid: passwordValid, reasons: passwordReasons } = usePasswordStrength(password);

  const passwordsMatch = password === confirmPassword && password.length > 0;

  // Explain why the submit button is disabled, so it never greys out silently.
  const submitDisabledReason = (() => {
    if (!password) return 'Enter a password to continue';
    if (!passwordValid) return passwordReasons.join('. ');
    if (!confirmPassword) return 'Confirm your password to continue';
    if (!passwordsMatch) return 'Passwords do not match';
    return null;
  })();

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength — surface the specific unmet requirement(s).
    if (!passwordValid) {
      setError(passwordReasons.join('. '));
      return;
    }

    const verifySearch = { email, invite: inviteToken, returnTo: search.returnTo };

    try {
      await signupMutation.mutateAsync({
        email,
        password,
        name: name || undefined,
        invite: inviteToken,
        returnTo: search.returnTo,
      });
      // On success, redirect to verification page (preserve invite token if present)
      navigate({ to: '/verify-email', search: verifySearch });
    } catch (err) {
      if (isVerificationEmailSendFailure(err)) {
        // The account exists but no email went out; resending lives on the verify page.
        navigate({ to: '/verify-email', search: { ...verifySearch, sendFailed: true } });
        return;
      }
      setError(authErrorMessage(err, 'An unexpected error occurred. Please try again.'));
    }
  };

  return (
    <ThemeProvider>
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background flex flex-col items-center justify-center p-6">
        <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:60px_60px]" />

        <Card variant="bordered" className="relative max-w-md w-full shadow-2xl">
          <CardContent className="p-8 space-y-8">
            {/* Logo/Brand Section */}
            <div className="text-center space-y-3">
              <div className="flex justify-center mb-2">
                <CofactorMark size={80} className="text-foreground" />
              </div>
              <Heading level={1} className="text-2xl font-bold text-foreground">
                Create your account
              </Heading>
              <Text className="text-muted-foreground">Get started with {brandName} testing</Text>
            </div>

            <Divider />

            {/* Sign Up Form */}
            <form onSubmit={handleSignup} className="space-y-4">
              {error && (
                <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                  <Text size="sm" className="text-destructive">
                    {error}
                  </Text>
                </div>
              )}

              <TextField
                label="Name (optional)"
                type="text"
                value={name}
                onChange={setName}
                placeholder="Your name"
                autoComplete="name"
              />

              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={setEmail}
                placeholder="you@example.com"
                autoComplete="email"
                isRequired
              />

              <div className="space-y-2">
                <PasswordField
                  label="Password"
                  value={password}
                  onChange={setPassword}
                  autoComplete="new-password"
                  isRequired
                />
                <PasswordStrengthIndicator password={password} />
              </div>

              <PasswordField
                label="Confirm Password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                autoComplete="new-password"
                isRequired
                errorMessage={
                  confirmPassword && !passwordsMatch ? 'Passwords do not match' : undefined
                }
              />

              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
                isDisabled={signupMutation.isPending || !passwordValid || !passwordsMatch}
              >
                {signupMutation.isPending ? (
                  <Flex align="center" gap="sm">
                    <Spinner size="sm" />
                    <span>Creating account...</span>
                  </Flex>
                ) : (
                  'Create account'
                )}
              </Button>

              {submitDisabledReason && !signupMutation.isPending && (
                <Text size="sm" className="text-center text-muted-foreground">
                  {submitDisabledReason}
                </Text>
              )}

              <div className="text-center">
                <Text size="sm" className="text-muted-foreground">
                  Already have an account?{' '}
                  {search.returnTo ? (
                    <a href={search.returnTo} className="text-primary hover:underline">
                      Sign in
                    </a>
                  ) : (
                    <Link
                      to="/login"
                      search={{ invite: inviteToken }}
                      className="text-primary hover:underline"
                    >
                      Sign in
                    </Link>
                  )}
                </Text>
              </div>
            </form>

            <Divider />

            <Text size="xs" className="text-center text-muted-foreground">
              By signing up, you agree to our{' '}
              <a
                href={termsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground transition-colors"
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                href={privacyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground transition-colors"
              >
                Privacy Policy
              </a>
            </Text>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-6 text-center">
          <Text size="xs" className="text-muted-foreground">
            © {new Date().getFullYear()} {brandName}. All rights reserved.
          </Text>
        </div>
      </div>
    </ThemeProvider>
  );
}
